// src/services/payroll/payrollDataService.ts

/**
 * Service für DB-Operationen auf customer_payroll_data
 * 
 * Speichert und liest aus DATEV-PDFs extrahierte Abrechnungsdaten.
 * Wird beim Finalisieren automatisch befüllt und später für
 * die Rechnungsgenerierung verwendet.
 */

import { supabase } from '../../lib/supabaseClient';
import { CustomerPayrollData } from '../../shared/types';

export interface SavePayrollDataInput {
  customerId: string;
  year: number;
  month: number;
  anzahlArbeitnehmer: number | null;
  anzahlArbeitnehmerMitEntgelt: number | null;
  hasNextMonthBeitragsnachweis: boolean;
  nextMonth: number | null;
  nextMonthYear: number | null;
  sourceFilename: string | null;
}

export const payrollDataService = {

  /**
   * Speichert oder aktualisiert Abrechnungsdaten für einen Kunden/Monat
   * Verwendet UPSERT auf den UNIQUE Constraint (customer_id, year, month)
   */
  savePayrollData: async (input: SavePayrollDataInput): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase
        .from('customer_payroll_data')
        .upsert(
          {
            customer_id: input.customerId,
            year: input.year,
            month: input.month,
            anzahl_arbeitnehmer: input.anzahlArbeitnehmer,
            anzahl_arbeitnehmer_mit_entgelt: input.anzahlArbeitnehmerMitEntgelt,
            has_next_month_beitragsnachweis: input.hasNextMonthBeitragsnachweis,
            next_month: input.nextMonth,
            next_month_year: input.nextMonthYear,
            analyzed_at: new Date().toISOString(),
            source_filename: input.sourceFilename,
            updated_at: new Date().toISOString()
          },
          {
            onConflict: 'customer_id,year,month'
          }
        );

      if (error) {
        console.error('[PayrollDataService] Fehler beim Speichern:', error);
        return { success: false, error: error.message };
      }

      console.log(`[PayrollDataService] Daten gespeichert für Kunde ${input.customerId}, ${input.month}/${input.year}`);
      return { success: true };

    } catch (error) {
      console.error('[PayrollDataService] Unerwarteter Fehler:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  },

  /**
   * Liest Abrechnungsdaten für einen bestimmten Kunden und Monat
   */
  getPayrollData: async (
    customerId: string,
    year: number,
    month: number
  ): Promise<CustomerPayrollData | null> => {
    try {
      const { data, error } = await supabase
        .from('customer_payroll_data')
        .select('*')
        .eq('customer_id', customerId)
        .eq('year', year)
        .eq('month', month)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Kein Eintrag gefunden
          return null;
        }
        console.error('[PayrollDataService] Fehler beim Laden:', error);
        return null;
      }

      return data as CustomerPayrollData;

    } catch (error) {
      console.error('[PayrollDataService] Unerwarteter Fehler:', error);
      return null;
    }
  },

  /**
   * Liest alle Abrechnungsdaten für einen Kunden (für Rechnungsübersicht)
   */
  getPayrollDataForCustomer: async (
    customerId: string
  ): Promise<CustomerPayrollData[]> => {
    try {
      const { data, error } = await supabase
        .from('customer_payroll_data')
        .select('*')
        .eq('customer_id', customerId)
        .order('year', { ascending: false })
        .order('month', { ascending: false });

      if (error) {
        console.error('[PayrollDataService] Fehler beim Laden:', error);
        return [];
      }

      return (data || []) as CustomerPayrollData[];

    } catch (error) {
      console.error('[PayrollDataService] Unerwarteter Fehler:', error);
      return [];
    }
  },

  /**
   * Liest Abrechnungsdaten für alle Kunden eines bestimmten Monats
   * (nützlich für Sammel-Rechnungserstellung)
   */
  getPayrollDataForMonth: async (
    year: number,
    month: number
  ): Promise<CustomerPayrollData[]> => {
    try {
      const { data, error } = await supabase
        .from('customer_payroll_data')
        .select('*')
        .eq('year', year)
        .eq('month', month)
        .order('customer_id');

      if (error) {
        console.error('[PayrollDataService] Fehler beim Laden:', error);
        return [];
      }

      return (data || []) as CustomerPayrollData[];

    } catch (error) {
      console.error('[PayrollDataService] Unerwarteter Fehler:', error);
      return [];
    }
  }
};
